import React, { useState, useMemo } from 'react';
import { useData } from '../DataContext';
import { api } from '../services/api';
import { X, Phone, Calendar, User } from 'lucide-react';

const Clients = () => {
  const { clients: allClients, loading, refreshClients } = useData();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  // Фильтруем только постоянных клиентов (не "Накд клиент")
  const clients = useMemo(() => 
    allClients.filter(c => !c.isWalkIn),
    [allClients]
  );

  const handleView = (client) => {
    setViewingClient(client);
    setIsEditing(false);
    setFormData({
      name: client.name,
      phone: client.phone || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (viewingClient && isEditing) {
        // Обновление существующего клиента
        if (typeof api.updateClient === 'function') {
          await api.updateClient(viewingClient._id, formData);
        } else if (typeof api.editClient === 'function') {
          await api.editClient(viewingClient._id, formData);
        } else {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/clients/${viewingClient._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(formData)
          });

          if (!response.ok) {
            throw new Error(`Ошибка обновления: ${response.status}`);
          }
        }
      } else {
        // Создание нового клиента
        await api.createClient(formData);
      }
      
      setShowModal(false);
      setViewingClient(null);
      setIsEditing(false);
      setFormData({
        name: '',
        phone: ''
      });
      await refreshClients();
    } catch (error) {
      console.error('Ошибка сохранения клиента:', error);
      alert('Ошибка при сохранении клиента');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!viewingClient) return;
    
    if (!window.confirm(`Вы уверены, что хотите удалить клиента "${viewingClient.name}"?`)) {
      return;
    }

    setSubmitting(true);
    try {
      if (typeof api.deleteClient === 'function') {
        await api.deleteClient(viewingClient._id);
      } else if (typeof api.delete === 'function') {
        await api.delete('clients', viewingClient._id);
      } else {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/clients/${viewingClient._id}`, {
          method: 'DELETE',
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });

        if (!response.ok) {
          throw new Error(`Ошибка удаления: ${response.status}`);
        }
      }

      setShowModal(false);
      setViewingClient(null);
      setIsEditing(false);
      await refreshClients();
    } catch (error) {
      console.error('Ошибка удаления клиента:', error);
      alert('Ошибка при удалении клиента: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredClients = useMemo(() =>
    clients.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.phone && c.phone.includes(search))
    ),
    [clients, search]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Клиенты</h1>
          <p className="text-gray-400 mt-1">Всего клиентов: {clients.length}</p>
        </div>
        <button
          onClick={() => {
            setViewingClient(null);
            setIsEditing(false);
            setFormData({ name: '', phone: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-2 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-800 font-semibold transition-all shadow-md" 
        >
          Добавить клиента
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3">
          <input
            type="text"
            placeholder="Поиск по имени или телефону..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-700/50">
          <thead className="bg-gray-700/30">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                №
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Имя
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Телефон
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Дата добавления
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filteredClients.map((client, index) => (
              <tr 
                key={client._id} 
                className="hover:bg-gray-700/20 transition-colors cursor-pointer"
                onClick={() => handleView(client)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {index + 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-emerald-600/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
                      <span className="text-emerald-400 font-semibold">
                        {client.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-white">{client.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {client.phone || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(client.createdAt).toLocaleDateString('ru-RU')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards - Стиль как на скриншоте */}
      <div className="md:hidden space-y-3">
        {filteredClients.map((client, index) => (
          <div 
            key={client._id} 
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 hover:border-gray-600/50 transition-all cursor-pointer"
            onClick={() => handleView(client)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base mb-1">{client.name}</h3>
                <p className="text-gray-500 text-xs">
                  {new Date(client.createdAt).toLocaleDateString('ru-RU', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric' 
                  })} {new Date(client.createdAt).toLocaleTimeString('ru-RU', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
            
            {client.phone && (
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Phone className="w-4 h-4" />
                <span>{client.phone}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <User className="w-3.5 h-3.5" />
              <span>Клиент #{index + 1}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {/* {filteredClients.length === 0 && (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <h3 className="text-xl font-semibold text-white mb-2">
            {search ? 'Клиенты не найдены' : 'Нет клиентов'}
          </h3>
          <p className="text-gray-400 mb-6">
            {search ? 'Попробуйте изменить параметры поиска' : 'Добавьте первого клиента'}
          </p>
          {!search && (
            <button 
              onClick={() => {
                setViewingClient(null);
                setIsEditing(false);
                setFormData({ name: '', phone: '' });
                setShowModal(true);
              }} 
              className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-all"
            >
              Добавить клиента
            </button>
          )}
        </div>
      )} */}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {viewingClient ? 'Информация о клиенте' : 'Новый клиент'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingClient(null);
                    setIsEditing(false);
                  }}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {viewingClient && !isEditing ? (
                <div className="space-y-4">
                  <div className="bg-gray-700/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 bg-emerald-600/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
                        <span className="text-emerald-400 font-bold text-2xl">
                          {viewingClient.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{viewingClient.name}</h3>
                        <p className="text-gray-400 text-sm">Клиент</p>
                      </div>
                    </div>
                    
                    {viewingClient.phone && (
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-600/50">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-white">{viewingClient.phone}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 pt-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-400 text-sm">
                        Добавлен: {new Date(viewingClient.createdAt).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleDelete}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? 'Удаление...' : 'Удалить'}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      Изменить
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Имя клиента *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      placeholder="Введите имя"
                      className="input-field"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Телефон
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+998 90 123 45 67"
                      className="input-field"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingClient) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 btn-secondary"
                      disabled={submitting}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingClient ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;